import React, { useEffect, useState } from 'react';
import {
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';

export interface IFormControlInput {
  placeholder?: string;
  value?: string;
  form?: any;
  name: string;
  className?: string;
  /** Pixel width, CSS string (`100%`), or omit for default 240px. */
  width?: number | string;
  height?: number;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onSearch?: (data: any) => void;
  style?: React.CSSProperties;
  label?: string;
  gap?: string;
  error?: string;
  touched?: boolean;
  isRequired?: boolean;
  type?: string;
  disabled?: boolean;
  isValidate?: boolean;
  isRead?: boolean;
  maxLength?: number;
  min?: number | string;
  max?: number | string;
  pattern?: string;
  leftIcon?: React.ReactNode;
  leftIconPlain?: boolean;
}

function resolveWidth(width: number | string | undefined): string {
  if (width == null) return '240px';
  return typeof width === 'number' ? `${width}px` : width;
}

const FormControlInput: React.FC<IFormControlInput> = (props) => {
  const { t } = useTranslation();
  const {
    placeholder,
    name,
    onChange,
    onFocus,
    style,
    form,
    className,
    width,
    label,
    gap,
    height,
    value,
    error,
    touched,
    type = 'text',
    disabled = false,
    isRequired: explicitIsRequired,
    maxLength,
    min,
    max,
    pattern,
    leftIcon,
    leftIconPlain = false,
  } = props;
  const widthCss = resolveWidth(width);
  const _height = height || 36;

  const [localValue, setLocalValue] = useState(value ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordField = type === 'password';

  useEffect(() => {
    if (!form && value !== undefined) {
      setLocalValue(value);
    }
  }, [value, form]);

  const inputValue = form ? (form.values?.[name] ?? '') : localValue;
  const inputError = form ? form.errors?.[name] : error;
  const inputTouched = form ? form.touched?.[name] : touched;
  const isInvalid = !!inputError && !!inputTouched;

  const isRequired =
    explicitIsRequired !== undefined
      ? explicitIsRequired
      : typeof form?.isRequired === 'function'
        ? form.isRequired(name)
        : false;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (pattern) {
      if (pattern.includes('[') && pattern.includes(']*')) {
        const allowedChars = pattern.match(/\[([^\]]+)\]/)?.[1] || '';
        newValue = newValue
          .split('')
          .filter((char) =>
            allowedChars.split('').some((allowed) => {
              if (allowed === '+') return char === '+';
              if (allowed === '0-9') return /\d/.test(char);
              return char === allowed;
            }),
          )
          .join('');
      }
    }

    if (form && !onChange) {
      form.setFieldValue(name, newValue);
      return;
    }

    if (!form) setLocalValue(newValue);
    onChange?.(newValue);
  };

  const handleBlur = () => {
    form?.setFieldTouched(name, true);
  };

  const inputType =
    isPasswordField && !showPassword
      ? 'password'
      : isPasswordField && showPassword
        ? 'text'
        : type;

  return (
    <FormControl
      className={`text-area-container ${className ?? ''}`}
      width={widthCss}
      style={style}
      isRequired={isRequired}
      isInvalid={isInvalid}
    >
      <Flex direction="column" gap={gap}>
        {label ? <FormLabel htmlFor={name}>{label}</FormLabel> : null}

        {props.isRead ? (
          <Text>{inputValue || '-'}</Text>
        ) : (
          <InputGroup size="md" width="100%">
            {leftIcon ? (
              <InputLeftElement
                position="absolute"
                left="1px"
                top="1px"
                height={leftIconPlain ? `${_height}px` : `${_height - 2}px`}
                width={leftIconPlain ? '36px' : '44px'}
                borderRight={leftIconPlain ? undefined : '1px solid'}
                borderColor={leftIconPlain ? undefined : 'gray.200'}
                bg={leftIconPlain ? 'transparent' : 'gray.50'}
                borderLeftRadius={leftIconPlain ? undefined : '5px'}
                pointerEvents="none"
              >
                {leftIcon}
              </InputLeftElement>
            ) : null}
            <Input
              id={name}
              type={inputType}
              placeholder={placeholder}
              name={name}
              width="100%"
              height={`${_height}px`}
              value={inputValue}
              onChange={handleChange}
              onBlur={handleBlur}
              onFocus={onFocus}
              disabled={disabled}
              autoComplete="off"
              maxLength={maxLength}
              min={min}
              max={max}
              pattern={pattern}
              pl={leftIcon ? (leftIconPlain ? '36px' : '54px') : undefined}
              pr={isPasswordField && inputValue ? '40px' : undefined}
            />
            {isPasswordField && inputValue ? (
              <InputRightElement height={`${_height}px`}>
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowPassword(!showPassword)}
                  variant="ghost"
                  size="sm"
                  tabIndex={-1}
                />
              </InputRightElement>
            ) : null}
          </InputGroup>
        )}

        {isInvalid && props.isValidate ? (
          <FormErrorMessage>{t(String(inputError))}</FormErrorMessage>
        ) : null}
      </Flex>
    </FormControl>
  );
};

export default FormControlInput;
